import styled from "styled-components";
import { localeToTranslations, type Locale } from "../intl/intl.utils";
import { ErrorPageContent } from "./ErrorPageContent";
import "@nectary/components/title";
import "@nectary/components/text";
import "@nectary/components/button";
import "@nectary/components/link";

type CrashPageProps = {
  error: unknown;
};

const CrashPage = ({ error }: CrashPageProps) => {
  // Needs to work outside of the Intl provider.
  const defaultTranslation = localeToTranslations(
    (window.meta?.intl?.locale || "en-US") as Locale
  );
  return (
    <ErrorPageContent>
      <CrashPageIllustration />
      <sinch-title
        text={defaultTranslation.crashPageHeading}
        type="xl"
        level="1"
      />
      <MaxWidth>
        <sinch-text type="m">{defaultTranslation.crashPageContent}</sinch-text>
      </MaxWidth>
      <ButtonRow>
        {location.pathname !== "/" && (
          <sinch-button
            size="l"
            type="secondary"
            on-click={() => history.pushState({}, "", "/")}
            aria-label={defaultTranslation.crashPageActionGoHome}
            text={defaultTranslation.crashPageActionGoHome}
          />
        )}
        <sinch-button
          size="l"
          type="primary"
          on-click={() =>
            window.location.assign(window.location.href + "?t=" + Date.now())
          }
          aria-label={defaultTranslation.crashPageActionRefreshPage}
          text={defaultTranslation.crashPageActionRefreshPage}
        />
      </ButtonRow>
      <div>
        <sinch-link
          text={defaultTranslation.crashPageReportIssue}
          aria-label={defaultTranslation.crashPageReportIssue}
          href={"https://tickets.sinch.com/plugins/servlet/desk/portal/126"}
        >
          {defaultTranslation.crashPageReportIssue}
        </sinch-link>
        {error instanceof Error ? (
          <sinch-text
            type="s"
            style={{ color: "var(--sinch-sys-color-text-muted)" }}
          >
            {defaultTranslation.crashPageErrorInfo.replace(
              "{errorId}",
              error.message
            )}
          </sinch-text>
        ) : null}
      </div>
    </ErrorPageContent>
  );
};

const MaxWidth = styled.div`
  max-width: 400px;
  margin: 0 auto;
`;

const ButtonRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  gap: 16px;
`;

const CrashPageIllustration = () => (
  <svg
    width={286}
    height={226}
    viewBox="0 0 286 226"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx={141.475} cy={112.649} r={112.527} fill="#FCECCB" />
    <mask
      id="mask0_889_42942"
      style={{
        maskType: "alpha",
      }}
      maskUnits="userSpaceOnUse"
      x={28}
      y={0}
      width={226}
      height={226}
    >
      <circle cx={141.471} cy={112.657} r={112.527} fill="#FCECCB" />
    </mask>
    <g mask="url(#mask0_889_42942)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M143.375 124.882L103.537 134.228C100.655 134.904 97.6687 135.006 94.7478 134.528C91.8269 134.05 88.3928 137.569 85.8772 136.009C78.5453 131.464 73.3603 125.922 76.1627 117.163C79.345 107.216 83.736 113.441 80.8257 101.036C77.9154 88.6314 80.0535 78.8495 92.3877 75.6533C104.722 72.4571 95.6695 62.5803 107.25 59.4577C115.03 57.3597 121.819 42.6468 123.958 36.9198C126.098 31.1928 132.975 28.7162 138.41 25.9173L145.879 22.0701C147.911 21.024 150.266 20.7976 152.46 21.4376C154.654 22.0776 156.518 23.5352 157.668 25.5097L165.371 38.7344L144.859 43.5504L143.298 63.9012L143.372 124.887L143.375 124.882Z"
        fill="black"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M141.822 128.016L174.669 140.089C177.551 140.765 180.537 140.867 183.458 140.389C186.379 139.911 186.207 133.308 188.723 131.748C203.096 131.748 196.902 124.32 203.096 119.113C209.29 113.905 206.205 100.46 194.936 96.7688C189.377 94.948 199.947 81.6171 186.138 77.9748C173.804 74.7786 186.249 61.9974 174.669 58.8748C166.889 56.7768 160.697 48.0288 158.558 42.3018C156.419 36.5748 152.222 31.8507 146.786 29.0518C144.403 27.8247 141.898 46.3051 140.338 46.685C138.778 47.0648 141.898 67.0357 141.898 67.0357L141.825 128.022L141.822 128.016Z"
        fill="black"
      />
      <path
        d="M107.717 102.842C97.2631 114.512 103.867 132.443 103.101 156.868C103.101 156.868 106.286 177.356 103.101 194.254C100.29 202.157 97.9844 215.028 95.4569 225.321C88.0668 255.415 182.852 225.126 189.075 225.321C180.322 183.713 180.322 155.261 180.322 155.261C180.322 155.261 189.618 154.26 193.8 150.197C194.339 149.674 194.384 148.861 194.043 148.192C190.039 140.343 183.298 121.496 180.054 112.302C175.028 98.0582 166.278 102.327 158.997 102.099L131.589 101.24C128.776 101.078 115.792 95.3238 107.717 102.842Z"
        fill="#FFBE3C"
      />
      <path
        d="M182.83 63.7322C184.928 55.3773 183.496 43.9832 180.732 35.7515L169.821 35.4095L173.371 50.9348L166.791 73.4305C164.797 87.047 165.852 76.4457 185.355 77.7216C181.858 75.7786 180.732 72.087 182.83 63.7322Z"
        fill="black"
      />
      <path
        d="M130.825 78.1959L130.139 100.06C136.384 108.162 144.494 111.269 149.46 110.635C154.426 110.002 155.075 104.071 154.482 101.037C153.414 98.3097 152.036 93.0099 154.075 90.5448C154.731 89.7508 155.61 89.0258 156.582 88.8457C157.553 88.6656 160.875 88.7841 165.344 87.4361C172.729 85.2084 175.952 71.9809 176.904 66.1C178.024 59.1777 177.997 43.0349 171.398 31.6128C166.584 23.2934 160.395 19.9824 150.827 20.7283C144.736 21.1907 139.033 23.8989 134.825 28.3275C130.617 32.7561 128.204 38.5895 128.053 44.6966L127.506 65.1452C127.387 69.7178 128.539 74.2338 130.833 78.1911L130.825 78.1959Z"
        fill="#A05735"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M137.997 57.7871C137.997 57.7871 141.035 76.5278 138.9 89.7667C138.466 95.0801 134.593 101.547 129.267 101.326L112.363 99.4355C116.539 92.5966 134.8 55.0294 137.997 57.7871Z"
        fill="black"
      />
      <path
        d="M139.502 74.807C139.502 74.807 126.275 84.4295 122.951 78.1911C119.205 71.1694 122.951 44.4974 122.951 44.4974C123.278 32.3087 130.131 21.7464 141.056 19.0447C149.502 16.956 150.771 17.1014 163.206 20.0159C173.698 21.376 179.393 21.8765 183.08 28.1669C185.486 32.2713 184.745 37.7426 180.762 40.3447C170.637 46.9591 148.828 50.5203 148.634 57.5149C148.44 64.5094 150.966 68.9782 139.502 74.807Z"
        fill="black"
      />
      <path
        d="M131.56 98.1889L127.147 100.116C126.707 100.308 126.441 100.766 126.524 101.239C127.723 108.117 134.034 117.185 136.356 120.34C136.783 120.92 137.637 120.91 138.069 120.334C139.584 118.314 142.811 114.053 145.281 111.09C145.878 110.375 145.415 109.039 144.566 108.656C140.865 106.985 135.432 102.385 132.991 98.6454C132.682 98.1715 132.079 97.9625 131.56 98.1889Z"
        fill="#FFDF9E"
      />
      <path
        d="M154.736 99.6013L159.2 101.222C159.539 101.346 159.798 101.627 159.87 101.98C161.031 107.654 158.494 115.262 157.069 118.893C156.734 119.747 155.571 119.778 155.137 118.971C153.976 116.813 151.987 113.342 149.856 110.632C149.226 109.83 149.869 108.18 150.679 107.559C152.428 106.218 153.202 103.481 153.095 100.926C153.059 100.054 153.916 99.3034 154.736 99.6013Z"
        fill="#FFDF9E"
      />
      <path
        d="M149.32 114.359C149.293 115.225 148.569 115.904 147.704 115.877C146.839 115.85 146.159 115.126 146.186 114.261C146.213 113.396 146.937 112.716 147.802 112.743C148.668 112.77 149.347 113.494 149.32 114.359Z"
        fill="#FFDF9E"
      />
      <path
        d="M149.291 123.505C149.264 124.37 148.54 125.05 147.675 125.022C146.81 124.995 146.13 124.272 146.157 123.406C146.184 122.541 146.908 121.862 147.773 121.889C148.639 121.916 149.318 122.639 149.291 123.505Z"
        fill="#FFDF9E"
      />
    </g>
    <path
      d="M41.0023 90.4873C49.13 92.3827 52.92 99.1691 53.276 100.02C41.7395 99.1984 18.4789 97.4952 17.729 97.2587C16.7915 96.9632 10.5969 90.4289 9.6025 89.503C8.07598 88.0816 8.59496 86.1548 11.4118 86.8631C12.6175 87.1663 12.9475 87.6328 13.7567 88.1942C13.5415 87.6514 14.0055 85.4235 17.2694 87.8909C20.5333 90.3584 21.5315 92.7447 24.1646 94.7848C25.7505 95.5353 27.9179 95.3265 31.0898 95.1819C36.5398 94.9334 28.9959 91.0882 27.6074 89.0179C25.4673 85.8271 27.974 84.9802 31.3533 86.7804C33.8618 88.1167 35.8033 89.2748 41.0023 90.4873Z"
      fill="#A05735"
    />
    <path
      d="M80.8879 144.397C82.8156 142.417 85.0951 138.707 85.0951 138.707C94.4275 139.828 107.075 152.891 107.075 152.891C100.038 160.886 91.2076 174.294 83.9017 176.775C81.0837 177.732 78.3643 177.281 75.1344 173.736C56.1191 152.867 57.1068 132.2 44.9094 111.809C43.1289 108.833 38.4028 105.805 37.0853 105.598C37.0853 105.598 22.1148 104.078 16.365 100.891C14.3169 99.5415 10.3055 94.2735 8.56871 92.7683C4.91268 89.6 8.4221 87.7096 12.4838 90.0095C14.0023 90.8694 17.4842 94.5297 20.4385 96.6636C26.9678 97.141 33.4739 95.6349 40.2608 94.9672C45.4533 94.4565 51.7326 96.8415 54.3653 101.648C61.9911 115.57 76.1736 140.585 80.8879 144.397Z"
      fill="#A05735"
    />
    <path
      d="M12.9744 92.933C10.9784 89.6705 14.0751 89.0865 16.5847 91.7917C17.2771 92.5381 18.4849 94.6539 19.7595 96.6419L23.8434 96.7699L17.0242 101.192C15.8041 99.6847 13.9227 94.4828 12.9744 92.933Z"
      fill="#A05735"
    />
    <path
      d="M84.4877 139.138C94.1884 141.789 103.499 150.609 106.838 155.182C128.59 134.959 113.075 99.1112 108.487 102.594C105.031 105.216 92.3417 126.044 83.6663 136.737C82.9924 137.567 83.4559 138.856 84.4877 139.138Z"
      fill="#FFBE3C"
    />
    <path
      d="M166.812 78.703C166.736 79.2825 165.367 82.9539 160.477 81.6209C157.367 80.7729 155.426 79.2893 156.848 77.2604C158.27 75.2316 161.059 74.361 164.169 75.209C167.279 76.057 166.889 78.1235 166.812 78.703Z"
      fill="white"
    />
    <path
      d="M249.636 97.0276C241.406 98.4102 237.198 104.946 236.79 105.773C248.355 105.675 271.677 105.432 272.44 105.243C273.394 105.007 279.986 98.8738 281.036 98.0119C282.649 96.6889 282.252 94.7335 279.396 95.2639C278.174 95.491 277.815 95.9359 276.972 96.4456C277.221 95.9173 276.897 93.6647 273.485 95.9228C270.073 98.181 268.928 100.5 266.172 102.371C264.542 103.021 262.392 102.677 259.235 102.334C253.812 101.744 261.582 98.3793 263.097 96.4001C265.433 93.3496 262.984 92.3474 259.499 93.9323C256.911 95.1089 254.901 96.1432 249.636 97.0276Z"
      fill="#A05735"
    />
    <path
      d="M206.453 148.334C204.653 146.236 202.61 142.391 202.61 142.391C193.226 142.925 179.784 155.17 179.784 155.17C186.307 163.59 194.281 177.525 201.417 180.459C204.169 181.59 206.911 181.31 210.357 177.975C230.642 158.337 230.951 137.65 244.402 118.063C246.365 115.204 251.271 112.478 252.599 112.354C252.599 112.354 267.636 111.775 273.574 108.954C275.702 107.735 280.036 102.729 281.863 101.336C285.711 98.4025 282.327 96.296 278.129 98.337C276.56 99.1001 272.855 102.535 269.773 104.48C263.227 104.547 256.828 102.637 250.096 101.545C244.946 100.71 238.529 102.697 235.601 107.329C227.118 120.746 211.396 144.824 206.453 148.334Z"
      fill="#A05735"
    />
    <path
      d="M278.129 98.337C276.56 99.1001 272.459 102.114 269.377 104.058L273.033 109.218L273.574 108.954C275.702 107.735 280.036 102.729 281.863 101.336C285.711 98.4025 282.327 96.296 278.129 98.337Z"
      fill="#A05735"
    />
    <path
      d="M277.456 101.224C279.653 98.0931 276.599 97.3162 273.924 99.859C273.187 100.561 271.849 102.597 270.452 104.501L266.368 104.373L272.897 109.213C274.209 107.785 276.413 102.712 277.456 101.224Z"
      fill="#A05735"
    />
    <path
      d="M203.189 142.858C193.342 144.896 183.497 153.116 179.878 157.47C159.435 135.925 169.262 104.226 175.019 104.406C178.358 104.511 195.488 129 204.136 140.535C204.778 141.39 204.237 142.641 203.189 142.858Z"
      fill="#FFBE3C"
    />
  </svg>
);

// lazy loaded
// eslint-disable-next-line no-restricted-syntax
export default CrashPage;
